import axios from 'axios'
import { getAdminToken } from '../utils/auth'
import { translate } from '../utils/i18n'

const DEFAULT_TIMEOUT = 15000
const RETRYABLE_STATUSES = new Set([502, 503, 504])
const RETRYABLE_METHODS = new Set(['get', 'head', 'options'])

function createRequestId() {
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function canRetry(error) {
  const config = error && error.config
  if (!config || config.__rocRetryCount >= 1 || !RETRYABLE_METHODS.has(String(config.method || 'get').toLowerCase())) {
    return false
  }
  if (!error.response) return error.code !== 'ERR_CANCELED'
  return RETRYABLE_STATUSES.has(error.response.status)
}

function retryDelay() {
  return new Promise(resolve => window.setTimeout(resolve, 450))
}

function englishMessage(message, fallback) {
  const translated = translate(String(message || '').trim())
  return translated && !/[\u3400-\u9fff]/.test(translated) ? translated : fallback
}

export function getApiErrorMessage(error, fallback = '请求失败，请稍后重试。') {
  const response = error && error.response
  const data = response && response.data
  const backendMessage = data && typeof data.msg === 'string' ? data.msg.trim() : ''
  const requestId = (data && data.requestId) || (response && response.headers && response.headers['x-request-id'])
  const suffix = requestId ? ` (Error ID: ${requestId})` : ''
  const fallbackMessage = englishMessage(fallback, 'Request failed. Please try again later.')
  if (backendMessage) return `${englishMessage(backendMessage, fallbackMessage)}${suffix}`

  const message = String((error && error.message) || '').toLowerCase()
  if (error && (error.code === 'ECONNABORTED' || message.includes('timeout'))) {
    return `Request timed out. Check the network and backend service, then try again.${suffix}`
  }
  if (error && error.code === 'ERR_CANCELED') return 'Request cancelled.'
  if (!response || message.includes('network error')) {
    return `Cannot connect to the service. Check the network or backend on port 8090.${suffix}`
  }
  if (response.status === 401 || response.status === 403) {
    return `Your session has expired. Sign in again.${suffix}`
  }
  if (response.status >= 500) return `Service is temporarily unavailable. Try again later.${suffix}`
  return `${fallbackMessage}${suffix}`
}

export function installHttpInterceptors(client) {
  if (client.__rocInterceptorsInstalled) return client
  client.__rocInterceptorsInstalled = true
  client.defaults.timeout = client.defaults.timeout || DEFAULT_TIMEOUT

  client.interceptors.request.use(config => {
    config.headers = config.headers || {}
    config.timeout = config.timeout || DEFAULT_TIMEOUT
    config.headers['X-Request-Id'] = config.headers['X-Request-Id'] || createRequestId()
    const url = config.url || ''
    if (url.includes('/admin/') && !url.endsWith('/admin/login')) {
      const token = getAdminToken()
      if (token) config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  client.interceptors.response.use(
    response => response,
    async error => {
      if (canRetry(error)) {
        error.config.__rocRetryCount = (error.config.__rocRetryCount || 0) + 1
        await retryDelay()
        return client(error.config)
      }
      error.userMessage = getApiErrorMessage(error)
      if (error.response && [401, 403].includes(error.response.status)) {
        const url = String(error.config && error.config.url || '')
        window.dispatchEvent(new CustomEvent(url.includes('/admin/') ? 'roc:admin-session-expired' : 'roc:user-session-expired'))
      }
      return Promise.reject(error)
    }
  )
  return client
}

export const http = installHttpInterceptors(axios.create({
  baseURL: process.env.VUE_APP_AUTH_BASE_URL || '',
  timeout: DEFAULT_TIMEOUT
}))
